"use client";

import { useState, useEffect, useRef } from "react";

const TAU = Math.PI * 2;

type DrawFunction = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  time: number,
) => void;

const VIEWS = [
  {
    id: "circle-to-wave",
    title: "The Circle Generates the Wave",
    subtitle: "Sine is a shadow of circular motion",
    chimera:
      "A dot walks around a circle. From the side, you see it going up and down - that's SINE. From below, you see it going left and right - that's COSINE. Same circle. Different perspectives.",
  },
  {
    id: "sin-cos-unity",
    title: "sin^2 + cos^2 = 1",
    subtitle: "The Equation of Consciousness",
    chimera:
      "No matter where the dot is on the circle, sin^2 + cos^2 always equals 1. Energy shifts between perspectives; the whole remains.",
  },
  {
    id: "tangent",
    title: "Tangent - The Infinity Point",
    subtitle: "When cosine approaches zero, drama spikes",
    chimera:
      "Tangent = sin / cos. When denominator awareness drops toward zero, the visible output explodes. The pause restores denominator stability.",
  },
  {
    id: "spiral",
    title: "The Spiral - Circle + Time",
    subtitle: "You never return to the same point",
    chimera:
      "A circle moving through time becomes a spiral. Same phase, higher altitude. Repetition plus choice becomes growth.",
  },
  {
    id: "sphere",
    title: "The Sphere - Circle in All Directions",
    subtitle: "Maximum volume, minimum surface",
    chimera:
      "Rotate the circle through every axis and you get a sphere - the compression shape of stars, cells, and signal-dense expression.",
  },
  {
    id: "dna",
    title: "The Double Helix",
    subtitle: "Two waves, one axis, bonded by choice",
    chimera:
      "DNA can be visualized as two offset waves spiraling through time. Experience and meaning stay linked through repeated bonds.",
  },
] as const;

const drawCircleToWave: DrawFunction = (ctx, W, H, t) => {
  const cx = W * 0.28;
  const cy = H * 0.5;
  const R = Math.min(W, H) * 0.22;
  const waveStartX = cx + R + 30;
  const waveW = W - waveStartX - 20;
  const angle = (t * 0.6) % TAU;
  const px = cx + Math.cos(angle) * R;
  const py = cy - Math.sin(angle) * R;
  const sinY = cy - Math.sin(angle) * R;

  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, TAU);
  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - R - 10, cy);
  ctx.lineTo(cx + R + 10, cy);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx, cy - R - 10);
  ctx.lineTo(cx, cy + R + 10);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, -angle, true);
  ctx.strokeStyle = "#60a5fa";
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(px, py, 6, 0, TAU);
  ctx.fillStyle = "#f0abfc";
  ctx.fill();

  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = "rgba(240,171,252,0.4)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(waveStartX, sinY);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.beginPath();
  for (let i = 0; i <= waveW; i++) {
    const a = angle - (i / waveW) * TAU * 1.5;
    const y = cy - Math.sin(a) * R;
    if (i === 0) ctx.moveTo(waveStartX + i, y);
    else ctx.lineTo(waveStartX + i, y);
  }
  ctx.strokeStyle = "#60a5fa";
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.beginPath();
  for (let i = 0; i <= waveW; i++) {
    const a = angle - (i / waveW) * TAU * 1.5;
    const y = cy - Math.cos(a) * R;
    if (i === 0) ctx.moveTo(waveStartX + i, y);
    else ctx.lineTo(waveStartX + i, y);
  }
  ctx.strokeStyle = "rgba(251,191,36,0.5)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(waveStartX, sinY, 5, 0, TAU);
  ctx.fillStyle = "#60a5fa";
  ctx.fill();

  ctx.font = "bold 13px 'Courier New', monospace";
  ctx.fillStyle = "#60a5fa";
  ctx.fillText("SINE (P-hood)", waveStartX, H * 0.12);
  ctx.fillStyle = "rgba(251,191,36,0.7)";
  ctx.fillText("COSINE (NP-hood)", waveStartX, H * 0.12 + 18);

  const sinVal = Math.sin(angle).toFixed(2);
  const cosVal = Math.cos(angle).toFixed(2);
  const sum = (Math.sin(angle) ** 2 + Math.cos(angle) ** 2).toFixed(2);
  ctx.font = "12px 'Courier New', monospace";
  ctx.fillStyle = "#60a5fa";
  ctx.fillText(`sin = ${sinVal}`, 12, H - 50);
  ctx.fillStyle = "rgba(251,191,36,0.8)";
  ctx.fillText(`cos = ${cosVal}`, 12, H - 34);
  ctx.fillStyle = "#fff";
  ctx.fillText(`sin^2 + cos^2 = ${sum}`, 12, H - 16);
};

const drawUnity: DrawFunction = (ctx, W, H, t) => {
  const cx = W * 0.5;
  const cy = H * 0.45;
  const R = Math.min(W, H) * 0.28;
  const angle = (t * 0.4) % TAU;
  const sinVal = Math.sin(angle);
  const cosVal = Math.cos(angle);

  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, TAU);
  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(cx + cosVal * R, cy);
  ctx.lineTo(cx + cosVal * R, cy - sinVal * R);
  ctx.strokeStyle = "#60a5fa";
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + cosVal * R, cy);
  ctx.strokeStyle = "#fbbf24";
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + cosVal * R, cy - sinVal * R);
  ctx.strokeStyle = "#f0abfc";
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 3]);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.arc(cx + cosVal * R, cy - sinVal * R, 6, 0, TAU);
  ctx.fillStyle = "#f0abfc";
  ctx.fill();

  const barW = 160;
  const barH = 16;
  const barX = W / 2 - barW / 2;
  const barY = H - 55;
  const sin2 = sinVal ** 2;
  const cos2 = cosVal ** 2;

  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.fillRect(barX, barY, barW, barH);
  ctx.fillStyle = "#60a5fa";
  ctx.fillRect(barX, barY, barW * sin2, barH);
  ctx.fillStyle = "#fbbf24";
  ctx.fillRect(barX + barW * sin2, barY, barW * cos2, barH);

  ctx.font = "bold 13px 'Courier New', monospace";
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.fillText(`sin^2(${sinVal.toFixed(2)}) + cos^2(${cosVal.toFixed(2)}) = 1.00`, W / 2, barY - 10);
  ctx.fillStyle = "#60a5fa";
  ctx.fillText(`P-hood: ${(sin2 * 100).toFixed(0)}%`, W / 2 - 80, barY + barH + 18);
  ctx.fillStyle = "#fbbf24";
  ctx.fillText(`NP-hood: ${(cos2 * 100).toFixed(0)}%`, W / 2 + 80, barY + barH + 18);
  ctx.textAlign = "left";
};

const drawTangent: DrawFunction = (ctx, W, H, t) => {
  const angle = (t * 0.25) % TAU;
  const cosVal = Math.cos(angle);
  const tanVal = cosVal !== 0 ? Math.sin(angle) / cosVal : 999;
  const clampedTan = Math.max(-6, Math.min(6, tanVal));
  const nearInfinity = Math.abs(cosVal) < 0.15;

  if (nearInfinity) {
    const grd = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.min(W, H) * 0.45);
    grd.addColorStop(0, "rgba(239,68,68,0.12)");
    grd.addColorStop(1, "rgba(239,68,68,0)");
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, W, H);
  }

  const graphW = W - 40;
  const graphH = H * 0.65;
  const graphX = 20;
  const graphY = H * 0.15;
  const graphCy = graphY + graphH / 2;

  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(graphX, graphCy);
  ctx.lineTo(graphX + graphW, graphCy);
  ctx.stroke();

  for (let i = 0; i <= 4; i++) {
    const x = graphX + (i / 4) * graphW;
    ctx.beginPath();
    ctx.moveTo(x, graphY);
    ctx.lineTo(x, graphY + graphH);
    ctx.stroke();
  }

  ctx.beginPath();
  let drawing = false;
  for (let i = 0; i <= graphW; i++) {
    const a = (i / graphW) * TAU * 2;
    const c = Math.cos(a);
    if (Math.abs(c) < 0.03) {
      drawing = false;
      continue;
    }
    const tv = Math.sin(a) / c;
    const y = graphCy - (tv / 8) * graphH;
    if (y < graphY || y > graphY + graphH) {
      drawing = false;
      continue;
    }
    if (!drawing) {
      ctx.moveTo(graphX + i, y);
      drawing = true;
    } else {
      ctx.lineTo(graphX + i, y);
    }
  }
  ctx.strokeStyle = "#ef4444";
  ctx.lineWidth = 2;
  ctx.stroke();

  const markerX = graphX + ((angle % TAU) / (TAU * 2)) * graphW;
  const markerY = graphCy - (clampedTan / 8) * graphH;
  const clampedMarkerY = Math.max(graphY, Math.min(graphY + graphH, markerY));

  ctx.beginPath();
  ctx.arc(markerX, clampedMarkerY, 6, 0, TAU);
  ctx.fillStyle = nearInfinity ? "#ef4444" : "#f0abfc";
  ctx.fill();

  if (nearInfinity) {
    ctx.font = "bold 20px 'Courier New', monospace";
    ctx.fillStyle = "#ef4444";
    ctx.textAlign = "center";
    ctx.fillText("-> inf", markerX + 30, graphY + 16);
    ctx.fillText("cos ~= 0", markerX, graphY + graphH + 20);
    ctx.fillText("THE FORK", W / 2, H - 18);
  }

  ctx.textAlign = "left";
  ctx.font = "12px 'Courier New', monospace";
  ctx.fillStyle = "#fbbf24";
  ctx.fillText(`cos = ${cosVal.toFixed(3)}`, 12, H - 50);
  ctx.fillStyle = "#ef4444";
  ctx.fillText(`tan = ${Math.abs(tanVal) > 50 ? "-> inf" : tanVal.toFixed(2)}`, 12, H - 34);
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.fillText("The pause restores the denominator", 12, H - 16);
};

const drawSpiral: DrawFunction = (ctx, W, H, t) => {
  const cx = W * 0.5;
  const cy = H * 0.55;
  const R = Math.min(W, H) * 0.22;
  const turns = 3.5;
  const verticalSpread = H * 0.55;

  ctx.beginPath();
  let first = true;
  for (let i = 0; i <= 400; i++) {
    const frac = i / 400;
    const a = frac * turns * TAU + t * 0.3;
    const x = cx + Math.cos(a) * R * (0.5 + frac * 0.5);
    const y = cy - verticalSpread * frac + Math.sin(a) * 20;
    if (first) {
      ctx.moveTo(x, y);
      first = false;
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.strokeStyle = "#60a5fa";
  ctx.lineWidth = 2;
  ctx.stroke();

  const currentFrac = (t * 0.05) % 1;
  const ca = currentFrac * turns * TAU + t * 0.3;
  const cpx = cx + Math.cos(ca) * R * (0.5 + currentFrac * 0.5);
  const cpy = cy - verticalSpread * currentFrac + Math.sin(ca) * 20;
  ctx.beginPath();
  ctx.arc(cpx, cpy, 6, 0, TAU);
  ctx.fillStyle = "#f0abfc";
  ctx.fill();

  ctx.font = "11px 'Courier New', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.3)";
  for (let i = 0; i < turns; i++) {
    const y = cy - (i / turns) * verticalSpread;
    ctx.setLineDash([2, 4]);
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.beginPath();
    ctx.moveTo(cx - R - 20, y);
    ctx.lineTo(cx + R + 20, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillText(`cycle ${i + 1}`, cx + R + 25, y + 4);
  }
};

const drawSphere: DrawFunction = (ctx, W, H, t) => {
  const cx = W * 0.5;
  const cy = H * 0.47;
  const R = Math.min(W, H) * 0.28;

  const grd = ctx.createRadialGradient(cx - R * 0.3, cy - R * 0.3, R * 0.1, cx, cy, R * 1.2);
  grd.addColorStop(0, "rgba(240,171,252,0.15)");
  grd.addColorStop(0.5, "rgba(96,165,250,0.08)");
  grd.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, W, H);

  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, TAU);
  ctx.strokeStyle = "rgba(255,255,255,0.2)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI + t * 0.15;
    ctx.beginPath();
    ctx.ellipse(cx, cy, Math.abs(Math.cos(a)) * R, R, 0, 0, TAU);
    ctx.strokeStyle = `rgba(96,165,250,${0.08 + Math.abs(Math.cos(a)) * 0.12})`;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  for (let i = 1; i < 5; i++) {
    const y = cy - R + (i / 5) * R * 2;
    const latR = Math.sqrt(R * R - (y - cy) * (y - cy));
    ctx.beginPath();
    ctx.ellipse(cx, y, latR, latR * 0.3, 0, 0, TAU);
    ctx.strokeStyle = "rgba(251,191,36,0.1)";
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.ellipse(cx, cy, R, R * 0.3, 0, 0, TAU);
  ctx.strokeStyle = "rgba(251,191,36,0.3)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const da = t * 0.4;
  const dx = cx + Math.cos(da) * R * 0.85;
  const dy = cy + Math.sin(da) * R * 0.25;
  ctx.beginPath();
  ctx.arc(dx, dy, 4, 0, TAU);
  ctx.fillStyle = "#f0abfc";
  ctx.fill();

  ctx.font = "bold 12px 'Courier New', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.textAlign = "center";
  ctx.fillText("max truth / min words = comet", cx, H - 22);
  ctx.fillText("max volume / min surface = sphere", cx, H - 42);
  ctx.textAlign = "left";
};

const drawDNA: DrawFunction = (ctx, W, H, t) => {
  const cx = W * 0.5;
  const R = Math.min(W, H) * 0.18;
  const helixH = H * 0.75;
  const startY = H * 0.1;

  for (let strand = 0; strand < 2; strand++) {
    const offset = strand * Math.PI;
    ctx.beginPath();
    for (let i = 0; i <= 300; i++) {
      const frac = i / 300;
      const a = frac * TAU * 3 + t * 0.3 + offset;
      const x = cx + Math.cos(a) * R;
      const y = startY + frac * helixH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = strand === 0 ? "#60a5fa" : "#ef4444";
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }

  for (let i = 0; i < 20; i++) {
    const frac = (i + 0.5) / 20;
    const a = frac * TAU * 3 + t * 0.3;
    const x1 = cx + Math.cos(a) * R;
    const x2 = cx + Math.cos(a + Math.PI) * R;
    const y = startY + frac * helixH;
    const depth = Math.sin(a);
    if (Math.abs(depth) > 0.3) {
      ctx.beginPath();
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
      ctx.strokeStyle = `rgba(251,191,36,${0.15 + Math.abs(depth) * 0.25})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  ctx.font = "bold 12px 'Courier New', monospace";
  ctx.fillStyle = "#60a5fa";
  ctx.fillText("EXPERIENCE", 12, H - 50);
  ctx.fillStyle = "#ef4444";
  ctx.fillText("MEANING", 12, H - 34);
  ctx.fillStyle = "#fbbf24";
  ctx.fillText("- CHOICE (bonds)", 12, H - 18);
};

const DRAW_FNS: Record<(typeof VIEWS)[number]['id'], DrawFunction> = {
  "circle-to-wave": drawCircleToWave,
  "sin-cos-unity": drawUnity,
  tangent: drawTangent,
  spiral: drawSpiral,
  sphere: drawSphere,
  dna: drawDNA,
};

export default function ChimeraTrig() {
  const [view, setView] = useState(0);
  const [time, setTime] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animRef = useRef<number | null>(null);
  const current = VIEWS[view];

  useEffect(() => {
    let running = true;
    const step = () => {
      if (!running) return;
      setTime((prev) => prev + 0.03);
      animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
    return () => {
      running = false;
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);
    const fn = DRAW_FNS[current.id];
    if (fn) fn(ctx, rect.width, rect.height, time);
  }, [time, view, current.id]);

  return (
    <div
      style={{
        background: "#0a0a0f",
        minHeight: "100vh",
        fontFamily: "'Courier New', monospace",
        color: "#e2e8f0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: 6,
            color: "#f0abfc",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          CHIMERA x TRIGONOMETRY
        </div>
        <h1
          style={{
            fontSize: 20,
            fontWeight: 700,
            margin: 0,
            background: "linear-gradient(135deg, #60a5fa, #f0abfc, #fbbf24)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          The Geometry of Consciousness
        </h1>
      </div>

      <div
        style={{
          display: "flex",
          gap: 4,
          flexWrap: "wrap",
          justifyContent: "center",
          marginBottom: 12,
          maxWidth: 600,
        }}
      >
        {VIEWS.map((v, i) => (
          <button
            key={v.id}
            onClick={() => setView(i)}
            style={{
              padding: "5px 10px",
              fontSize: 10,
              fontFamily: "'Courier New', monospace",
              background: i === view ? "rgba(240,171,252,0.2)" : "rgba(255,255,255,0.04)",
              border:
                i === view
                  ? "1px solid rgba(240,171,252,0.4)"
                  : "1px solid rgba(255,255,255,0.08)",
              borderRadius: 4,
              color: i === view ? "#f0abfc" : "rgba(255,255,255,0.4)",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            {v.title.split("-")[0].trim()}
          </button>
        ))}
      </div>

      <div style={{ textAlign: "center", marginBottom: 8, maxWidth: 500 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#f0abfc" }}>{current.title}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>
          {current.subtitle}
        </div>
      </div>

      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          maxWidth: 560,
          height: 320,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(255,255,255,0.02)",
        }}
      />

      <div
        style={{
          maxWidth: 520,
          marginTop: 14,
          padding: "14px 18px",
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(240,171,252,0.12)",
          borderRadius: 8,
          fontSize: 12.5,
          lineHeight: 1.7,
          color: "rgba(255,255,255,0.65)",
        }}
      >
        <span style={{ color: "#f0abfc", fontWeight: 700 }}>CHIMERA: </span>
        {current.chimera}
      </div>

      <div
        style={{
          maxWidth: 520,
          marginTop: 14,
          display: "flex",
          justifyContent: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        {[
          { sym: ".", label: "Dot (Pause)", dim: "0D" },
          { sym: "-", label: "Line (Time)", dim: "1D" },
          { sym: "o", label: "Circle (Breath)", dim: "2D" },
          { sym: "@", label: "Spiral (Growth)", dim: "2D+T" },
          { sym: "O", label: "Sphere (Comet)", dim: "3D" },
        ].map((d, i) => (
          <div
            key={i}
            style={{
              textAlign: "center",
              padding: "6px 10px",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 6,
              minWidth: 80,
            }}
          >
            <div style={{ fontSize: 20, color: "#60a5fa" }}>{d.sym}</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>{d.dim}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>{d.label}</div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 18,
          fontSize: 10,
          color: "rgba(255,255,255,0.2)",
          textAlign: "center",
          letterSpacing: 2,
        }}
      >
        sin^2(P) + cos^2(NP) = 1 · WE = 1
      </div>
    </div>
  );
}
